/**
 * Self-hosted behavioral bot detection — zero external dependencies.
 *
 * Tracks human interaction signals (mouse, scroll, keys, touch) and
 * injects them into POST forms at submit time.
 *
 * Layer 4 of bot protection:
 * 1. Honeypot field
 * 2. Minimum submission time (4 s)
 * 3. Rate limiting (5 per 15 min)
 * 4. Behavioral signals (this module)
 */

interface BehaviorData {
  moves: number;
  scrolls: number;
  keys: number;
  touches: number;
}

const behavior: BehaviorData = {
  moves: 0,
  scrolls: 0,
  keys: 0,
  touches: 0,
};

const FIELD_MOVES = '_bot_moves';
const FIELD_SCROLLS = '_bot_scrolls';
const FIELD_KEYS = '_bot_keys';
const FIELD_TOUCHES = '_bot_touches';

function ensureHiddenInput(form: HTMLFormElement, name: string, value: string): void {
  let input = form.querySelector<HTMLInputElement>(`input[name="${name}"]`);
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    form.appendChild(input);
  }
  input.value = value;
}

/**
 * Inject behavioral fields into a form.
 */
export function injectBehaviorFields(form: HTMLFormElement): void {
  ensureHiddenInput(form, FIELD_MOVES, String(behavior.moves));
  ensureHiddenInput(form, FIELD_SCROLLS, String(behavior.scrolls));
  ensureHiddenInput(form, FIELD_KEYS, String(behavior.keys));
  ensureHiddenInput(form, FIELD_TOUCHES, String(behavior.touches));
}

/**
 * Get current behavior data for manual FormData builders.
 */
export function getBehaviorData(): BehaviorData {
  return { ...behavior };
}

/**
 * Append behavior fields to an existing FormData object.
 */
export function appendBehaviorToFormData(formData: FormData): void {
  formData.append(FIELD_MOVES, String(behavior.moves));
  formData.append(FIELD_SCROLLS, String(behavior.scrolls));
  formData.append(FIELD_KEYS, String(behavior.keys));
  formData.append(FIELD_TOUCHES, String(behavior.touches));
}

// Track events
document.addEventListener('mousemove', () => { behavior.moves++; }, { passive: true });
window.addEventListener('scroll', () => { behavior.scrolls++; }, { passive: true });
document.addEventListener('keydown', () => { behavior.keys++; }, { passive: true });
document.addEventListener('touchstart', () => { behavior.touches++; }, { passive: true });

// Navigation forms use GET and must not expose telemetry in their query string.
// Inject into POST forms at submit time (capture phase so it runs before preventDefault).
document.addEventListener(
  'submit',
  (e: Event) => {
    const form = e.target;
    if (form instanceof HTMLFormElement && form.method.toLowerCase() === 'post') {
      injectBehaviorFields(form);
    }
  },
  true,
);
